<div class="container px-4 pb-4">
    <div class="card border-0 shadow-sm mb-3 py-1 px-4">
        <dx-toolbar class="theme-dependent">
            <dxi-item location="before">
                <div class="header-text">User Profile</div>
            </dxi-item>
            <dxi-item location="after" locateInMenu="never">
                <dx-button
                    text="Cancel"
                    stylingMode="outlined"
                    type="normal"
                    [disabled]="!isDataChanged"
                    (onClick)="cancel()"
                >
                </dx-button>
            </dxi-item>
            <dxi-item location="after" locateInMenu="never">
                <dx-button
                    [disabled]="!isDataChanged"
                    text="Save"
                    icon="save"
                    type="default"
                    stylingMode="contained"
                    (onClick)="save()"
                >
                </dx-button>
            </dxi-item>
            <dxi-item location="after" locateInMenu="never" [visible]="!!nestleConfirmAction">
                <dx-button
                    [text]="nestleConfirmAction?.text ?? ''"
                    type="success"
                    stylingMode="contained"
                    [disabled]="isActionRunning"
                    (onClick)="confirmNestleStep()"
                >
                </dx-button>
            </dxi-item>
            <dxi-item location="after" locateInMenu="never" [visible]="canConfirmTermination">
                <dx-button
                    text="Confirm Termination"
                    type="danger"
                    stylingMode="contained"
                    [disabled]="isActionRunning"
                    (onClick)="openStatusUpdate('confirmTermination')"
                >
                </dx-button>
            </dxi-item>
            <dxi-item location="after" locateInMenu="never" [visible]="canConfirmDeactivation">
                <dx-button
                    text="Confirm Deactivation"
                    stylingMode="contained"
                    [elementAttr]="{ class: 'button-warning' }"
                    [disabled]="isActionRunning"
                    (onClick)="openStatusUpdate('confirmDeactivation')"
                >
                </dx-button>
            </dxi-item>
            <dxi-item location="after" locateInMenu="never" [visible]="canConfirmPasswordReset">
                <dx-button
                    text="Confirm Reset"
                    stylingMode="contained"
                    [elementAttr]="{ class: 'button-warning' }"
                    [disabled]="isActionRunning"
                    (onClick)="isPasswordResetPopupOpened = true"
                >
                </dx-button>
            </dxi-item>
            <dxi-item location="after" locateInMenu="never" [visible]="moreMenuActions.length > 0">
                <dx-drop-down-button
                    text="More"
                    icon="overflow"
                    stylingMode="outlined"
                    type="default"
                    displayExpr="text"
                    keyExpr="id"
                    itemTemplate="moreMenuItem"
                    [splitButton]="false"
                    [dropDownOptions]="{ width: 210 }"
                    [items]="moreMenuActions"
                    [disabled]="isActionRunning"
                    (onItemClick)="onMoreActionClick($event)"
                >
                    <div *dxTemplate="let item of 'moreMenuItem'" class="d-flex align-items-center gap-2"
                         [class.text-danger]="item.color === 'danger'" [class.text-success]="item.color === 'success'">
                        <i class="dx-icon {{ item.icon }}" [class.text-danger]="item.color === 'danger'" [class.text-success]="item.color === 'success'"></i>
                        <span>{{ item.text }}</span>
                    </div>
                </dx-drop-down-button>
            </dxi-item>
        </dx-toolbar>
    </div>

    <dx-load-panel
        container=".view-wrapper-scroll"
        [visible]="isLoading"
        [showPane]="false"
        [position]="{ of: 'user-profile' }"
    >
    </dx-load-panel>

    <dx-validation-group #validationGroup>
        @if (isFormVisible) {
            <div class="form-container">
                @if (authService.canViewProfileSection(userId, PermissionType.UserContactInformationOwn, PermissionType.UserContactInformationOthers)) {
                    <app-contact-info
                        [userId]="userId"
                        [forceReadOnly]="isAdminProfileReadOnly"
                        (isDataChanged)="onFieldChange()"
                        (changePasswordRequested)="changePassword()"
                    ></app-contact-info>
                }

                @if (authService.canViewProfileSection(userId, PermissionType.UserEmergencyDetailsOwn, PermissionType.UserEmergencyDetailsOthers)) {
                    <app-emergency-details
                        [userId]="userId"
                        [forceReadOnly]="isAdminProfileReadOnly"
                        (isDataChanged)="onFieldChange()"
                    ></app-emergency-details>
                }

                @if (isEmployee) {
                    @if (authService.canViewProfileSection(userId, PermissionType.EmployeeShiftDetailsOwn, PermissionType.EmployeeShiftDetailsOthers)) {
                        <app-shift-details
                            [userId]="userId"
                            [forceReadOnly]="isAdminProfileReadOnly"
                            (isDataChanged)="onFieldChange()"
                        ></app-shift-details>
                    }

                    @if (authService.canViewProfileSection(userId, PermissionType.EmployeeFinancialDetailsOwn, PermissionType.EmployeeFinancialDetailsOthers)) {
                        <app-financial-details [userId]="userId"></app-financial-details>
                    }

                    @if (authService.canViewProfileSection(userId, PermissionType.EmployeeEmploymentDetailsOwn, PermissionType.EmployeeEmploymentDetailsOthers)) {
                        <app-employment-details
                            [userId]="userId"
                            [forceReadOnly]="isAdminProfileReadOnly"
                            (isDataChanged)="onFieldChange()"
                            (systemAccessChanged)="nestleSystemAccess = $event"
                        ></app-employment-details>
                    }

                        @if (authService.canViewProfileSection(userId, PermissionType.EmployeeNestleDetailsOwn, PermissionType.EmployeeNestleDetailsOthers)) {
                            <app-nestle-details
                                [userId]="userId"
                                [forceReadOnly]="isAdminProfileReadOnly"
                                [systemAccess]="nestleSystemAccess"
                                [status]="nestleStatus"
                                (isDataChanged)="onFieldChange()"
                            ></app-nestle-details>
                        }
                    }

                @if (isSupplier$ | async) {
                    @if (authService.canViewProfileSection(userId, PermissionType.UserSupplierDetailsOwn, PermissionType.UserSupplierDetailsOthers)) {
                        <app-supplier-details
                            [userId]="userId"
                            [forceReadOnly]="isAdminProfileReadOnly"
                            (isDataChanged)="onFieldChange()"
                        ></app-supplier-details>
                    }
                }

                @if (isCarrier$ | async) {
                    @if (authService.canViewProfileSection(userId, PermissionType.UserCarrierDetailsOwn, PermissionType.UserCarrierDetailsOthers)) {
                        <app-carrier-details
                            [userId]="userId"
                            [forceReadOnly]="isAdminProfileReadOnly"
                            (isDataChanged)="onFieldChange()"
                        ></app-carrier-details>
                    }
                }

                @if (isVendor$ | async) {
                    @if (authService.canViewProfileSection(userId, PermissionType.UserVendorDetailsOwn, PermissionType.UserVendorDetailsOthers)) {
                        <app-vendor-details
                            [userId]="userId"
                            [forceReadOnly]="isAdminProfileReadOnly"
                            (isDataChanged)="onFieldChange()"
                        ></app-vendor-details>
                    }
                }

                @if (isCustomer$ | async) {
                    @if (authService.canViewProfileSection(userId, PermissionType.UserCustomerDetailsOwn, PermissionType.UserCustomerDetailsOthers)) {
                        <app-customer-details
                            [userId]="userId"
                            [forceReadOnly]="isAdminProfileReadOnly"
                            (isDataChanged)="onFieldChange()"
                        ></app-customer-details>
                    }
                }

                @if (isFinance$ | async) {
                    @if (authService.canViewProfileSection(userId, PermissionType.UserSiteDetailsOwn, PermissionType.UserSiteDetailsOthers)) {
                        <app-site-details
                            [userId]="userId"
                            [forceReadOnly]="isAdminProfileReadOnly"
                            (isDataChanged)="onFieldChange()"
                        ></app-site-details>
                    }
                }

                @if (isAgency$ | async) {
                    @if (authService.canViewProfileSection(userId, PermissionType.UserAgencyDetailsOwn, PermissionType.UserAgencyDetailsOthers)) {
                        <app-agency-details
                            [userId]="userId"
                            [forceReadOnly]="isAdminProfileReadOnly"
                            (isDataChanged)="onFieldChange()"
                        ></app-agency-details>
                    }
                }

                @if (authService.canViewProfileSection(userId, PermissionType.UserNotesOwn, PermissionType.UserNotesOthers)) {
                    <app-notes
                        [entityId]="userId"
                        [noteType]="NoteType.Employee"
                        [canCreate]="authService.canEditProfileSection(userId, PermissionType.UserNotesOwn, PermissionType.UserNotesOthers)"
                    ></app-notes>
                }

                @if (authService.canViewProfileSection(userId, PermissionType.UserSecurityAndPermissionsOwn, PermissionType.UserSecurityAndPermissionsOthers)) {
                    <app-security-settings
                        [userId]="userId"
                        [forceReadOnly]="isAdminProfileReadOnly"
                        (isDataChanged)="onFieldChange()"
                        (rolesChanged)="rolesChanged($event)"
                    ></app-security-settings>
                }
            </div>
        }
    </dx-validation-group>
</div>

<change-profile-password-form
    [userId]="userId"
    [(visible)]="isChangePasswordPopupOpened"
></change-profile-password-form>

<user-status-update-popup
    [userId]="userId"
    [mode]="statusUpdateMode"
    [(visible)]="isStatusUpdatePopupOpened"
    (confirmed)="refreshStatus()"
></user-status-update-popup>

<issue-password-popup
    [userId]="userId"
    [(visible)]="isIssuePasswordPopupOpened"
></issue-password-popup>

<form-popup
    titleText="Confirm Password Reset"
    saveText="Confirm"
    [width]="500"
    [(visible)]="isPasswordResetPopupOpened"
    [isSaveDisabled]="isActionRunning"
    (save)="confirmPasswordReset()"
>
    <div class="text-center">
        You are confirming that this employee's password has been reset. Once confirmed, the password reset date will be extended by 3 months.
        <div class="fw-bold">This action cannot be undone.</div>
    </div>
</form-popup>
